import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Repeat, TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface ShareClass {
  id: string;
  name: string;
  class_type: string;
  authorized_shares: number;
  is_preferred: boolean | null;
}

interface CapTableEntry {
  shareholder_id: string;
  shareholder_name: string;
  total_shares: number;
}

interface ShareClassConversionCalculatorProps {
  companyId: string;
}

export const ShareClassConversionCalculator = ({
  companyId,
}: ShareClassConversionCalculatorProps) => {
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [capTable, setCapTable] = useState<CapTableEntry[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [sharesToConvert, setSharesToConvert] = useState('');
  const [conversionRatio, setConversionRatio] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch share classes
      const { data: classesData, error: classesError } = await supabase
        .from('share_classes')
        .select('*')
        .eq('company_id', companyId);

      if (classesError) throw classesError;
      setShareClasses(classesData || []);

      // Fetch cap table
      const { data: capTableData, error: capTableError } = await supabase
        .from('cap_table_summary')
        .select('*')
        .eq('company_id', companyId);

      if (capTableError) throw capTableError;
      setCapTable(capTableData || []);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const selectedClass = shareClasses.find((c) => c.id === selectedClassId);
  const convertedShares = parseInt(sharesToConvert) * conversionRatio || 0;
  const totalShares = capTable.reduce((sum, entry) => sum + entry.total_shares, 0);
  const newTotalShares = totalShares - parseInt(sharesToConvert || '0') + convertedShares;

  // Calculate ownership changes
  const currentOwnership =
    totalShares > 0 ? ((parseInt(sharesToConvert || '0') / totalShares) * 100).toFixed(2) : '0.00';
  const newOwnership =
    newTotalShares > 0 ? ((convertedShares / newTotalShares) * 100).toFixed(2) : '0.00';
  const ownershipChange = parseFloat(newOwnership) - parseFloat(currentOwnership);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Repeat className="h-5 w-5 text-primary" />
          Share Class Conversion Calculator
        </CardTitle>
        <CardDescription>
          Model the impact of converting preferred shares to common shares
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="shareClass">Share Class to Convert</Label>
            <Select value={selectedClassId} onValueChange={setSelectedClassId}>
              <SelectTrigger>
                <SelectValue placeholder="Select share class" />
              </SelectTrigger>
              <SelectContent>
                {shareClasses
                  .filter((c) => c.is_preferred)
                  .map((shareClass) => (
                    <SelectItem key={shareClass.id} value={shareClass.id}>
                      {shareClass.name}
                      <Badge variant="outline" className="ml-2">
                        {shareClass.class_type}
                      </Badge>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sharesToConvert">Shares to Convert</Label>
            <Input
              id="sharesToConvert"
              type="number"
              value={sharesToConvert}
              onChange={(e) => setSharesToConvert(e.target.value)}
              placeholder="Enter number of shares"
              min="0"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="conversionRatio">Conversion Ratio</Label>
          <div className="flex items-center gap-2">
            <Input
              id="conversionRatio"
              type="number"
              step="0.01"
              value={conversionRatio}
              onChange={(e) => setConversionRatio(parseFloat(e.target.value) || 1)}
              min="0"
              className="max-w-[200px]"
            />
            <span className="text-sm text-muted-foreground">
              preferred shares = {conversionRatio} common shares
            </span>
          </div>
        </div>

        {selectedClass && sharesToConvert && parseInt(sharesToConvert) > 0 && (
          <>
            <Separator />

            <div className="space-y-4">
              <h3 className="font-semibold">Conversion Summary</h3>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Converting From</p>
                  <p className="text-2xl font-bold">{parseInt(sharesToConvert).toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">{selectedClass.name}</p>
                </div>

                <div className="flex items-center justify-center">
                  <Repeat className="h-8 w-8 text-muted-foreground" />
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Converting To</p>
                  <p className="text-2xl font-bold text-primary">
                    {convertedShares.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">Common Shares</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="font-medium">Ownership Impact</h4>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Current Ownership</span>
                      <TrendingDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-bold">{currentOwnership}%</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {parseInt(sharesToConvert).toLocaleString()} of {totalShares.toLocaleString()}{' '}
                      shares
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">
                        Post-Conversion Ownership
                      </span>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-bold">{newOwnership}%</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {convertedShares.toLocaleString()} of {newTotalShares.toLocaleString()} shares
                    </p>
                  </div>
                </div>

                <div
                  className={`flex items-start gap-2 p-3 rounded-lg ${
                    ownershipChange > 0
                      ? 'bg-green-50 dark:bg-green-950'
                      : ownershipChange < 0
                        ? 'bg-red-50 dark:bg-red-950'
                        : 'bg-muted/50'
                  }`}
                >
                  <AlertCircle className="h-5 w-5 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      Ownership Change: {ownershipChange > 0 ? '+' : ''}
                      {ownershipChange.toFixed(2)}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {ownershipChange > 0
                        ? 'Conversion increases ownership due to favorable conversion ratio'
                        : ownershipChange < 0
                          ? 'Conversion decreases ownership due to unfavorable conversion ratio'
                          : 'Conversion maintains current ownership percentage'}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="font-medium">Cap Table Summary</h4>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Shares (Current)</p>
                    <p className="font-semibold">{totalShares.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Shares (After)</p>
                    <p className="font-semibold">{newTotalShares.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Preferred Shares Reduction</p>
                    <p className="font-semibold text-red-600">
                      -{parseInt(sharesToConvert || '0').toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Common Shares Addition</p>
                    <p className="font-semibold text-green-600">
                      +{convertedShares.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950">
                <AlertCircle className="h-5 w-5 mt-0.5 text-blue-600 dark:text-blue-400" />
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  This is a modeling tool only. No actual conversion will be executed. To execute a
                  conversion, create a new ledger event with the appropriate share adjustments.
                </p>
              </div>
            </div>
          </>
        )}

        {(!selectedClass || !sharesToConvert || parseInt(sharesToConvert) === 0) && (
          <div className="text-center py-8 text-muted-foreground">
            Select a preferred share class and enter the number of shares to convert to see the
            impact analysis
          </div>
        )}
      </CardContent>
    </Card>
  );
};
